/**
 * sfu_client.ts — VOICE ROOMS, one connection to the SFU (2026-09-24). The worker is server/workers/voice-sfu/sfu.mjs
 * (mediasoup); the rules are lib/voice_room_rules.ts; the window is AtlasVoiceRoomsModule.
 *
 * ONE SESSION = ONE SEAT. `connect()` presents the one-use ticket (from `joinVoiceRoom`), loads the router's
 * capabilities into a mediasoup-client Device, opens a RECV WebRTC transport (and a SEND one unless the session is
 * LISTEN-ONLY), and from then on the SFU pushes a consumer for every speaker this client may hear (never a blocked one
 * — the SFU decides). `startMic()` produces ONE Opus track (mono, FEC, DTX — the voice defaults, stated in
 * VOICE_OPUS), opening the send transport then if the session joined without one.
 *
 * LISTEN-ONLY (2026-09-28): a player without a microphone — or who declines it — joins as a listener: no
 * getUserMedia, a receive-only transport, every voice heard. The microphone can be added later (`startMic`).
 *
 * THE PLAYBACK (the ONE audio runtime law): each remote voice is a MediaStreamAudioSourceNode → a per-voice GainNode
 * (the listener's own volume for that person) → a deafen gain → the runtime's `voice` bus input the caller passes in.
 * Chromium delivers silence to a MediaStreamAudioSourceNode fed by a remote WebRTC track unless the stream is ALSO
 * attached to a (muted) media element — so each voice keeps one, and a reference to it (a collected element starves
 * the node).
 *
 * The session never retries on its own: `onClosed` says why, and the module asks for a fresh ticket and reconnects
 * within the seat's grace.
 *
 * TURN (the gaps wave, round 2, 2026-09-28): the hello answer carries ICE servers with ephemeral TURN credentials when
 * the SFU has a relay configured; both transports use them. `relay: true` (the `atlas:voice-relay` lever, the probes)
 * forces a RELAYED path (`iceTransportPolicy: 'relay'`) — how a network that blocks UDP and TCP to the media port
 * reaches the SFU at all.
 * RESUMES ARE COALESCED (same wave): a join into a room of n answers n consumers at once; the first resume goes up at
 * once and every consumer that lands while a `resumeConsumers` message is in flight rides the NEXT one (an older SFU
 * that does not know it gets them one by one). No timer: a hidden tab's timers are throttled (a second; a minute under
 * Chrome's intensive throttling) and a resume must never wait on one.
 */
import type { Device } from 'mediasoup-client';
import type { Transport, Producer, Consumer, RtpCapabilities } from 'mediasoup-client/types';
import { VOICE_OPUS } from '../voice_room_rules.js';

export interface SfuPeer { identity: string; role: string; serverMuted: boolean; selfMuted: boolean }
export interface SfuHello {
  me: string; roomId: number; name: string; mode: string; peers: SfuPeer[];
  routerRtpCapabilities: RtpCapabilities;
  /** TURN relays with ephemeral credentials (none when the SFU has no relay configured). */
  iceServers?: RTCIceServer[];
}
export interface SfuYou { role: string; serverMuted: boolean; mode: string; canSpeak: boolean }
export interface SfuEvents {
  peerJoined?(p: SfuPeer): void;
  peerLeft?(identity: string): void;
  peerState?(p: SfuPeer): void;
  levels?(list: Array<[string, number]>): void;
  activeSpeaker?(identity: string): void;
  you?(s: SfuYou): void;
  voiceAdded?(identity: string): void;
  voiceRemoved?(identity: string): void;
  closed?(reason: string, kicked: boolean): void;
  state?(s: SfuState): void;
}
export type SfuState = 'connecting' | 'connected' | 'reconnecting' | 'closed';

interface Voice { identity: string; consumer: Consumer; element: HTMLAudioElement; source: MediaStreamAudioSourceNode; gain: GainNode; analyser: AnalyserNode }
interface Pending { resolve(v: unknown): void; reject(e: Error): void; timer: number }

const REQUEST_TIMEOUT_MS = 12_000;

export class SfuSession {
  readonly url: string;
  private ws: WebSocket | null = null;
  private seq = 1;
  private pending = new Map<number, Pending>();
  private device: Device | null = null;
  private sendT: Transport | null = null;
  private recvT: Transport | null = null;
  private producer: Producer | null = null;
  private voices = new Map<string, Voice>(); // consumerId → voice
  private volumes = new Map<string, number>(); // identity → 0..2
  private pausedConsumers = new Set<string>(); // consumerIds whose speaker is paused at the SFU
  /** Consumer ids the SFU closed (2026-09-25): a `consumerClosed` that lands while `addVoice` awaits its consume found no
   *  voice, and the voice was then installed for a dead consumer (a stream that just stops — DTX comfort noise); a
   *  late `consumerPaused` / `consumerResumed` for one is ignored rather than growing `pausedConsumers`. */
  private closedConsumers = new Set<string>();
  private deafGain: GainNode;
  private closedFlag = false;
  private kicked = false;
  state: SfuState = 'connecting';
  hello: SfuHello | null = null;
  /** The last error a request came back with (for the window and the probes). */
  lastError = '';
  /** Force a relayed path (TURN only) — set by `connect(…, { relay })`. */
  private relay = false;
  /** Consumer ids waiting for the coalesced resume; `resumeInFlight` while a `resumeConsumers` message is out. */
  private resumeQueue: Array<{ id: string; done: () => void; fail: (e: Error) => void }> = [];
  private resumeInFlight = false;
  private batchResume = true;

  constructor(url: string, private readonly ctx: AudioContext, output: AudioNode, private readonly events: SfuEvents) {
    this.url = url;
    this.deafGain = ctx.createGain();
    this.deafGain.connect(output);
  }

  // ── Signaling ──────────────────────────────────────────────────────────────
  private request<T>(method: string, data: unknown = {}): Promise<T> {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error('Not connected to the voice server'));
    const id = this.seq++;
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => { this.pending.delete(id); reject(new Error(`${method} timed out`)); }, REQUEST_TIMEOUT_MS);
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      ws.send(JSON.stringify({ id, method, data }));
    });
  }

  private onMessage(ev: MessageEvent): void {
    let msg: { id?: number; ok?: boolean; data?: unknown; error?: string; notify?: string };
    try { msg = JSON.parse(String(ev.data)); } catch { return; }
    if (typeof msg.id === 'number') {
      const p = this.pending.get(msg.id);
      if (!p) return;
      this.pending.delete(msg.id);
      window.clearTimeout(p.timer);
      if (msg.ok) p.resolve(msg.data); else { this.lastError = msg.error ?? 'refused'; p.reject(new Error(this.lastError)); }
      return;
    }
    // A failed consume / resume (the socket closing under it) is not an unhandled rejection (2026-09-25).
    if (msg.notify) void this.onNotify(msg.notify, msg.data as Record<string, unknown>).catch((e: unknown) => { this.lastError = String((e as Error)?.message ?? e); });
  }

  private async onNotify(kind: string, d: Record<string, unknown>): Promise<void> {
    switch (kind) {
      case 'newConsumer': await this.addVoice(d as never); break;
      case 'consumerClosed': this.removeVoice(String(d.consumerId)); break;
      case 'consumerPaused': this.setVoicePaused(String(d.consumerId), true); break;
      case 'consumerResumed': this.setVoicePaused(String(d.consumerId), false); break;
      case 'peerJoined': this.events.peerJoined?.(d as unknown as SfuPeer); break;
      case 'peerLeft': this.events.peerLeft?.(String(d.identity)); break;
      case 'peerState': this.events.peerState?.(d as unknown as SfuPeer); break;
      case 'levels': this.events.levels?.((d.list as Array<[string, number]>) ?? []); break;
      case 'activeSpeaker': this.events.activeSpeaker?.(String(d.identity)); break;
      case 'you': this.events.you?.(d as unknown as SfuYou); break;
      case 'kicked': this.kicked = true; this.lastError = String(d.reason ?? ''); break;
      default: break;
    }
  }

  private setState(s: SfuState): void { if (this.state !== s) { this.state = s; this.events.state?.(s); } }

  // ── Joining ────────────────────────────────────────────────────────────────
  /** Open the socket, present the ticket, load the Device and open both transports. */
  async connect(ticket: string, opts: { send?: boolean; relay?: boolean } = {}): Promise<SfuHello> {
    this.setState('connecting');
    this.relay = !!opts.relay;
    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(this.url);
      this.ws = ws;
      const t = window.setTimeout(() => reject(new Error('The voice server did not answer')), 8000);
      ws.onopen = () => { window.clearTimeout(t); resolve(); };
      ws.onerror = () => { window.clearTimeout(t); reject(new Error('Cannot reach the voice server')); };
      ws.onmessage = (e) => this.onMessage(e);
      ws.onclose = (e) => this.onSocketClosed(e);
    });
    const hello = await this.request<SfuHello>('hello', { ticket });
    this.hello = hello;
    const { Device } = await import('mediasoup-client');
    const device = new Device();
    await device.load({ routerRtpCapabilities: hello.routerRtpCapabilities });
    this.device = device;
    // A listen-only join opens no send transport (2026-09-28): nothing to send, and one less ICE link to hold.
    if (opts.send !== false) this.sendT = await this.openTransport('send');
    this.recvT = await this.openTransport('recv');
    await this.request('setRtpCapabilities', { rtpCapabilities: device.recvRtpCapabilities });
    this.setState('connected');
    return hello;
  }

  private async openTransport(direction: 'send' | 'recv'): Promise<Transport> {
    const params = await this.request<{ id: string; iceParameters: never; iceCandidates: never; dtlsParameters: never }>('createTransport', { direction });
    // TURN (2026-09-28): the relays the hello handed us; `relay` forces a relayed path (a network that blocks the port).
    const ice = this.hello?.iceServers ?? [];
    const opts = { ...params, ...(ice.length ? { iceServers: ice } : {}), ...(this.relay ? { iceTransportPolicy: 'relay' as RTCIceTransportPolicy } : {}) };
    const t = direction === 'send' ? this.device!.createSendTransport(opts) : this.device!.createRecvTransport(opts);
    t.on('connect', ({ dtlsParameters }, ok, fail) => {
      this.request('connectTransport', { transportId: t.id, dtlsParameters }).then(() => ok(), (e) => fail(e as Error));
    });
    if (direction === 'send') {
      t.on('produce', ({ kind, rtpParameters }, ok, fail) => {
        this.request<{ id: string }>('produce', { transportId: t.id, kind, rtpParameters }).then((r) => ok({ id: r.id }), (e) => fail(e as Error));
      });
    }
    let restarted = false;
    t.on('connectionstatechange', (s) => {
      if (s === 'failed' || s === 'disconnected') {
        if (!restarted) {
          restarted = true;
          this.setState('reconnecting');
          this.request<{ iceParameters: never }>('restartIce', { transportId: t.id })
            .then((r) => t.restartIce({ iceParameters: r.iceParameters }))
            .catch(() => { /* the socket will close and the module reconnects */ });
        } else if (s === 'failed') this.close('The audio link failed');
      }
      if (s === 'connected') { restarted = false; this.setState('connected'); }
    });
    return t;
  }

  /** Send the microphone (produced PAUSED until `setMicOpen(true)`). */
  async startMic(track: MediaStreamTrack): Promise<void> {
    if (this.producer || this.closedFlag || !this.device) return;
    // A listener taking up the microphone: the send transport is opened now (2026-09-28).
    if (!this.sendT) this.sendT = await this.openTransport('send');
    if (this.producer || this.closedFlag) return;
    this.producer = await this.sendT.produce({
      track, stopTracks: false, zeroRtpOnPause: true,
      codecOptions: { opusStereo: VOICE_OPUS.stereo, opusDtx: VOICE_OPUS.dtx, opusFec: VOICE_OPUS.fec, opusMaxAverageBitrate: VOICE_OPUS.maxAverageBitrate, opusPtime: VOICE_OPUS.ptime },
    });
    this.producer.pause();
    await this.request('setSelfMuted', { muted: true });
  }

  /** Open or close the microphone (push-to-talk, self-mute). The SFU still holds it shut for a moderator's mute. */
  async setMicOpen(open: boolean): Promise<void> {
    if (!this.producer) return;
    if (open) this.producer.resume(); else this.producer.pause();
    try { await this.request('setSelfMuted', { muted: !open }); } catch { /* the socket is closing */ }
  }
  get micOpen(): boolean { return !!this.producer && !this.producer.paused; }
  /** The media links' ICE/DTLS states (signaling can be up while a link is not): 'new' … 'connected' | 'failed'. */
  get links(): { send: string; recv: string } { return { send: this.sendT?.connectionState ?? 'none', recv: this.recvT?.connectionState ?? 'none' }; }
  /** The local candidate type of each transport's selected pair ('host' | 'srflx' | 'relay' | '' before it connects) —
   *  how a probe proves a RELAYED path (the gaps wave, round 2). */
  async candidateTypes(): Promise<{ send: string; recv: string }> {
    const typeOf = async (t: Transport | null): Promise<string> => {
      if (!t) return 'none';
      try {
        const stats = await t.getStats();
        let pairLocal = '';
        const locals = new Map<string, string>();
        stats.forEach((r: { type: string; id: string; candidateType?: string; state?: string; nominated?: boolean; selected?: boolean; localCandidateId?: string }) => {
          if (r.type === 'local-candidate' && r.candidateType) locals.set(r.id, r.candidateType);
          if (r.type === 'candidate-pair' && (r.selected || (r.nominated && r.state === 'succeeded')) && r.localCandidateId) pairLocal = r.localCandidateId;
        });
        return locals.get(pairLocal) ?? '';
      } catch { return ''; }
    };
    return { send: await typeOf(this.sendT), recv: await typeOf(this.recvT) };
  }
  get hasMic(): boolean { return !!this.producer; }

  /** Swap the microphone (a new device) without renegotiating. */
  async replaceTrack(track: MediaStreamTrack): Promise<void> { await this.producer?.replaceTrack({ track }); }

  // ── Voices ─────────────────────────────────────────────────────────────────
  private async addVoice(d: { consumerId: string; producerId: string; identity: string; kind: string; rtpParameters: never; producerPaused?: boolean }): Promise<void> {
    if (!this.recvT || this.closedFlag || this.closedConsumers.has(d.consumerId)) return;
    if (d.producerPaused) this.pausedConsumers.add(d.consumerId);
    const consumer = await this.recvT.consume({ id: d.consumerId, producerId: d.producerId, kind: 'audio', rtpParameters: d.rtpParameters });
    // Closed while the consume was in flight (by the SFU, or the session itself): never install a dead voice (2026-09-25).
    if (this.closedFlag || this.closedConsumers.has(d.consumerId)) {
      this.pausedConsumers.delete(d.consumerId);
      try { consumer.close(); } catch { /* closed */ }
      return;
    }
    // A pause may have been announced while the consume was in flight: the set holds it.
    if (this.pausedConsumers.has(d.consumerId)) consumer.pause();
    const stream = new MediaStream([consumer.track]);
    const element = new Audio();
    element.muted = true; // Chromium starves the Web Audio node of a remote track unless a media element plays it too
    element.srcObject = stream;
    void element.play().catch(() => { /* muted autoplay is allowed; a refusal still leaves the node fed on most builds */ });
    const source = this.ctx.createMediaStreamSource(stream);
    const gain = this.ctx.createGain();
    gain.gain.value = this.volumes.get(d.identity) ?? 1;
    const analyser = this.ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(gain);
    gain.connect(this.deafGain);
    gain.connect(analyser);
    this.voices.set(d.consumerId, { identity: d.identity, consumer, element, source, gain, analyser });
    await this.resume(d.consumerId);
    this.events.voiceAdded?.(d.identity);
  }

  /** COALESCED RESUMES (the gaps wave, round 2): the first resume goes up at once; every consumer that lands while a
   *  `resumeConsumers` message is in flight rides the next (≤ 50 a message — the SFU's cap). A join into a full room
   *  answers ~49 consumers; one message each crowded the SFU's per-socket budget. */
  private resume(consumerId: string): Promise<void> {
    if (!this.batchResume) return this.request('resumeConsumer', { consumerId }).then(() => undefined);
    return new Promise<void>((done, fail) => {
      this.resumeQueue.push({ id: consumerId, done, fail });
      if (!this.resumeInFlight) void this.flushResumes();
    });
  }
  private async flushResumes(): Promise<void> {
    this.resumeInFlight = true;
    try {
      while (this.resumeQueue.length && !this.closedFlag) {
        const batch = this.resumeQueue.splice(0, 50);
        try {
          await this.request('resumeConsumers', { consumerIds: batch.map((b) => b.id) });
          for (const b of batch) b.done();
        } catch (e) {
          if (!/Unknown method/.test(String((e as Error)?.message ?? e))) { for (const b of batch) b.fail(e as Error); continue; }
          // An older SFU without the batch method: one resume per consumer from now on.
          this.batchResume = false;
          for (const b of [...batch, ...this.resumeQueue.splice(0, this.resumeQueue.length)]) {
            this.request('resumeConsumer', { consumerId: b.id }).then(() => b.done(), (err) => b.fail(err as Error));
          }
        }
      }
    } finally { this.resumeInFlight = false; }
  }

  /** The speaker paused or resumed at the SFU: disable or enable the track locally (a disabled track is silence —
   *  never the decoder's comfort noise from the last DTX descriptor). */
  private setVoicePaused(consumerId: string, paused: boolean): void {
    if (this.closedConsumers.has(consumerId)) return; // a late notice for a closed consumer (2026-09-25)
    if (paused) this.pausedConsumers.add(consumerId); else this.pausedConsumers.delete(consumerId);
    const v = this.voices.get(consumerId);
    if (!v) return;
    if (paused) v.consumer.pause(); else v.consumer.resume();
  }

  private removeVoice(consumerId: string): void {
    this.closedConsumers.add(consumerId); // even with no voice yet: its consume may still be in flight (2026-09-25)
    this.pausedConsumers.delete(consumerId);
    const v = this.voices.get(consumerId);
    if (!v) return;
    this.voices.delete(consumerId);
    try { v.source.disconnect(); v.gain.disconnect(); v.analyser.disconnect(); } catch { /* gone */ }
    v.element.srcObject = null;
    try { v.consumer.close(); } catch { /* closed */ }
    this.events.voiceRemoved?.(v.identity);
  }

  /** The listener's own volume for one person (0 = off … 2 = double). */
  setVolume(identity: string, v: number): void {
    const g = Math.max(0, Math.min(2, v));
    this.volumes.set(identity, g);
    for (const voice of this.voices.values()) if (voice.identity === identity) voice.gain.gain.setTargetAtTime(g, this.ctx.currentTime, 0.02);
  }
  volumeOf(identity: string): number { return this.volumes.get(identity) ?? 1; }

  /** Deafen: hear no one (the microphone is the caller's to close too). */
  setDeafened(d: boolean): void { this.deafGain.gain.setTargetAtTime(d ? 0 : 1, this.ctx.currentTime, 0.02); }

  /** Who this client is hearing right now, with each voice's RMS at the listener (the probes measure delivery). */
  hearing(): Array<{ identity: string; rms: number; paused: boolean; enabled: boolean; trackMuted: boolean; elementPaused: boolean; gain: number }> {
    const buf = new Float32Array(512);
    const out: Array<{ identity: string; rms: number; paused: boolean; enabled: boolean; trackMuted: boolean; elementPaused: boolean; gain: number }> = [];
    for (const v of this.voices.values()) {
      v.analyser.getFloatTimeDomainData(buf);
      let s = 0;
      for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
      out.push({ identity: v.identity, rms: Math.sqrt(s / buf.length), paused: v.consumer.paused, enabled: v.consumer.track.enabled, trackMuted: v.consumer.track.muted, elementPaused: v.element.paused, gain: Math.round(v.gain.gain.value * 1000) / 1000 });
    }
    return out;
  }
  get voiceCount(): number { return this.voices.size; }

  async stats(): Promise<unknown> { return this.request('stats'); }

  // ── Closing ────────────────────────────────────────────────────────────────
  private onSocketClosed(e: CloseEvent): void {
    for (const p of this.pending.values()) { window.clearTimeout(p.timer); p.reject(new Error('The voice server closed the connection')); }
    this.pending.clear();
    if (this.closedFlag) return;
    this.teardown();
    const reason = this.kicked ? this.lastError || 'Removed' : e.reason || (e.code === 4003 ? this.lastError || 'Refused' : 'The connection dropped');
    this.setState('closed');
    this.events.closed?.(reason, this.kicked);
  }

  private teardown(): void {
    this.closedFlag = true;
    for (const b of this.resumeQueue.splice(0, this.resumeQueue.length)) b.fail(new Error('The session closed'));
    for (const id of [...this.voices.keys()]) this.removeVoice(id);
    try { this.producer?.close(); } catch { /* closed */ }
    try { this.sendT?.close(); } catch { /* closed */ }
    try { this.recvT?.close(); } catch { /* closed */ }
    this.producer = null; this.sendT = null; this.recvT = null;
    // Here, not only in close(): a server-side close (a drop, a kick) left one deafen GainNode wired to the voice bus
    // per reconnect (2026-09-25).
    try { this.deafGain.disconnect(); } catch { /* gone */ }
  }

  /** Leave: tell the SFU, close everything. */
  close(reason = 'You left'): void {
    if (this.closedFlag) return;
    try { this.ws?.send(JSON.stringify({ id: this.seq++, method: 'leave', data: {} })); } catch { /* closed */ }
    this.teardown();
    try { this.ws?.close(1000, 'bye'); } catch { /* closed */ }
    this.setState('closed');
    this.events.closed?.(reason, false);
  }
}
